import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Wallet, CheckCircle2, X, MessageCircle, Plus, Upload, FileText, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import adminApi from "@/lib/adminApi";
import { buildPaymentRequestMessage, buildPaymentConfirmMessage, openWhatsApp } from "@/lib/whatsapp";
import { PAY_METHODS, MethodFields } from "@/components/MiDinero";

const RECEIPT_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "application/pdf"];
const RECEIPT_ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";
const MAX_RECEIPT_BYTES = 5 * 1024 * 1024;
const fileToData = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
const validateReceipt = (file) => {
  if (!file) return false;
  if (!RECEIPT_TYPES.includes(file.type)) { toast.error("Tipo no permitido. Usa JPG, PNG, WEBP o PDF"); return false; }
  if (file.size > MAX_RECEIPT_BYTES) { toast.error("El archivo supera el límite de 5 MB"); return false; }
  return true;
};
const openPaymentReceipt = async (pid) => {
  const w = window.open("", "_blank");
  try {
    const r = await adminApi.get(`/admin/payments/${pid}/receipt`);
    const data = r.data.receipt;
    if (!data) { if (w) w.close(); toast.error("Sin comprobante"); return; }
    const isData = typeof data === "string" && data.startsWith("data:");
    const url = isData ? URL.createObjectURL(await (await fetch(data)).blob()) : data;
    if (w) w.location.href = url; else window.open(url, "_blank");
    if (isData) setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch { if (w) w.close(); toast.error("Sin comprobante"); }
};

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("es", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`);
const fmtDate = (iso) => { if (!iso) return "—"; try { const hasTime = typeof iso === "string" && iso.length > 10; const d = new Date(hasTime ? iso : iso + "T00:00:00"); const b = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`; return hasTime ? `${b} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` : b; } catch { return iso; } };
const METHOD_LABEL = Object.fromEntries(PAY_METHODS);
const PST = {
  PENDIENTE: ["Pendiente", "bg-amber-50 text-amber-700 border-amber-200"],
  CONFIRMADO: ["Confirmado", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  CANCELADO: ["Cancelado", "bg-zinc-100 text-zinc-500 border-zinc-200"],
};
const FILTERS = [["PENDIENTE", "Pendientes"], ["CONFIRMADO", "Confirmados"], ["CANCELADO", "Cancelados"], ["TODOS", "Todos"]];

function detailLines(p) {
  const d = p.details || {};
  const out = [];
  if (p.method === "EFECTIVO") { if (d.pickup_name) out.push(["Retira", `${d.pickup_name}${d.pickup_phone ? ` · ${d.pickup_phone}` : ""}${d.pickup_document ? ` · ${d.pickup_document}` : ""}`]); }
  if (p.method === "ZELLE") { if (d.recipient) out.push(["Destinatario", d.recipient]); if (d.zelle_contact) out.push(["Zelle", d.zelle_contact]); }
  if (p.method === "WIRE") { if (d.beneficiary) out.push(["Beneficiario", d.beneficiary]); if (d.bank) out.push(["Banco", d.bank]); if (d.reference) out.push(["Referencia", d.reference]); }
  if (p.method === "USDT") { if (d.network) out.push(["Red", d.network]); if (d.wallet) out.push(["Wallet", d.wallet]); }
  if (p.method === "OTRO") { if (d.method_desc) out.push(["Método", d.method_desc]); if (d.instructions) out.push(["Instrucciones", d.instructions]); }
  return out;
}

function ConfirmDialog({ payment, onClose, onDone }) {
  const [reference, setReference] = useState("");
  const [txid, setTxid] = useState("");
  const [note, setNote] = useState("");
  const [proof, setProof] = useState(null);
  const [saving, setSaving] = useState(false);
  const isCash = payment.method === "EFECTIVO";
  const pickFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader(); r.onload = () => setProof(r.result); r.readAsDataURL(f);
  };
  const submit = async () => {
    setSaving(true);
    try {
      const r = await adminApi.post(`/admin/payments/${payment.id}/confirm`, { reference: reference || null, txid: txid || null, note: note || null, proof });
      toast.success("Pago confirmado");
      onDone(r.data); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto" data-testid="pay-confirm-dialog">
        <DialogHeader><DialogTitle>{isCash ? "Confirmar entrega de efectivo" : "Confirmar pago enviado"}</DialogTitle></DialogHeader>
        <div className="space-y-2.5 text-sm">
          <p className="text-base font-bold text-slate-900">{money(payment.amount)} · {METHOD_LABEL[payment.method]}</p>
          <p className="text-xs text-slate-500">{payment.owner_name} ({payment.owner_code}) · {payment.id}</p>
          <p className="text-xs text-slate-500">{isCash ? "Confirma que el dinero fue entregado a la persona autorizada." : "Confirma que PuntoCell realizó el pago."}</p>
          <label className="flex flex-col gap-0.5"><span className="text-[11px] text-slate-500 px-1">{payment.method === "USDT" ? "Referencia" : "Referencia / Nº operación"}</span>
            <Input data-testid="confirm-reference" value={reference} onChange={(e) => setReference(e.target.value)} className="h-10 rounded-lg" /></label>
          {(payment.method === "USDT" || payment.method === "WIRE") && <label className="flex flex-col gap-0.5"><span className="text-[11px] text-slate-500 px-1">TxID / Hash</span>
            <Input data-testid="confirm-txid" value={txid} onChange={(e) => setTxid(e.target.value)} className="h-10 rounded-lg" /></label>}
          <label className="flex flex-col gap-0.5"><span className="text-[11px] text-slate-500 px-1">Nota</span>
            <Input data-testid="confirm-note" value={note} onChange={(e) => setNote(e.target.value)} className="h-10 rounded-lg" /></label>
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer rounded-lg border border-dashed border-slate-300 px-3 py-2 hover:bg-slate-50">
            <Upload className="w-4 h-4" /> {proof ? "Comprobante adjunto ✓" : "Adjuntar comprobante (opcional)"}
            <input data-testid="confirm-proof" type="file" accept="image/*,application/pdf" onChange={pickFile} className="hidden" />
          </label>
        </div>
        <Button data-testid="confirm-submit" onClick={submit} disabled={saving} className="h-11 rounded-xl bg-slate-900 hover:bg-slate-800">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar pago/entrega"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function AdminCreateDialog({ onClose, onDone }) {
  const [owners, setOwners] = useState([]);
  const [ownerId, setOwnerId] = useState("");
  const [account, setAccount] = useState(null);
  const [method, setMethod] = useState("EFECTIVO");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [details, setDetails] = useState({});
  const [saving, setSaving] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const setD = (k, v) => setDetails((p) => ({ ...p, [k]: v }));
  useEffect(() => { adminApi.get("/admin/owners").then((r) => setOwners(r.data)).catch(() => {}); }, []);
  useEffect(() => { setAccount(null); if (ownerId) adminApi.get(`/admin/owners/${ownerId}/account`).then((r) => setAccount(r.data)).catch(() => {}); }, [ownerId]);
  const amt = Number(amount) || 0;
  const disp = account?.disponible ?? 0;
  const invalid = !ownerId || amt <= 0 || amt > disp + 1e-6;
  const submit = async () => {
    setSaving(true);
    try {
      const r = await adminApi.post("/admin/payments", { owner_id: ownerId, method, amount: amt, note: note || null, details });
      if (receiptFile) {
        try { await adminApi.post(`/admin/payments/${r.data.id}/receipt`, { data: await fileToData(receiptFile), filename: receiptFile.name }); }
        catch { toast.error("Pago creado, pero el comprobante no se pudo adjuntar"); }
      }
      toast.success("Registro creado"); onDone(r.data); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto" data-testid="pay-admin-create-dialog">
        <DialogHeader><DialogTitle>Registrar pago / retiro</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className="flex flex-col gap-0.5"><span className="text-[11px] text-slate-500 px-1">Consignador *</span>
            <select data-testid="admin-pay-owner" value={ownerId} onChange={(e) => setOwnerId(e.target.value)} className="h-10 rounded-lg border border-slate-200 text-sm px-2">
              <option value="">Selecciona…</option>
              {owners.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.code})</option>)}
            </select></label>
          {account && <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5 text-xs flex justify-between" data-testid="admin-pay-account"><span className="text-slate-500">Disponible</span><span className="font-semibold">{money(account.disponible)}</span></div>}
          <div className="grid grid-cols-3 gap-1.5">
            {PAY_METHODS.map(([k, l]) => <button key={k} data-testid={`admin-pay-method-${k}`} onClick={() => setMethod(k)} className={`h-9 rounded-lg text-xs font-medium border ${method === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>{l}</button>)}
          </div>
          <label className="flex flex-col gap-0.5"><span className="text-[11px] text-slate-500 px-1">Monto *</span>
            <Input data-testid="admin-pay-amount" type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-11 rounded-lg text-lg font-semibold" /></label>
          <MethodFields method={method} details={details} setD={setD} />
          <label className="flex flex-col gap-0.5"><span className="text-[11px] text-slate-500 px-1">Nota</span>
            <Input data-testid="admin-pay-note" value={note} onChange={(e) => setNote(e.target.value)} className="h-10 rounded-lg" /></label>
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer rounded-lg border border-dashed border-slate-300 px-3 py-2 hover:bg-slate-50">
            <Upload className="w-4 h-4" /> {receiptFile ? `Comprobante: ${receiptFile.name}` : "Adjuntar comprobante (opcional)"}
            <input data-testid="admin-pay-receipt" type="file" accept={RECEIPT_ACCEPT} onChange={(e) => { const f = e.target.files?.[0]; if (f && validateReceipt(f)) setReceiptFile(f); e.target.value = ""; }} className="hidden" />
          </label>
          {amt > disp + 1e-6 && <p className="text-xs text-rose-600" data-testid="admin-pay-over">El monto excede el disponible ({money(disp)}).</p>}
          <Button data-testid="admin-pay-submit" onClick={submit} disabled={saving || invalid} className="h-11 w-full rounded-xl bg-slate-900 hover:bg-slate-800">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registrar (queda pendiente)"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PaymentsAdminTab({ canConfirm }) {
  const [status, setStatus] = useState("PENDIENTE");
  const [rows, setRows] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [create, setCreate] = useState(false);
  const load = (s = status) => { setRows(null); adminApi.get(`/admin/payments?status=${s}`).then((r) => setRows(r.data)).catch(() => setRows([])); };
  useEffect(() => { load(status); /* eslint-disable-next-line */ }, [status]);
  const cancel = async (id) => {
    if (!window.confirm("¿Cancelar esta solicitud?")) return;
    try { await adminApi.post(`/admin/payments/${id}/cancel`, { reason: "Cancelado por PuntoCell" }); toast.success("Cancelada"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const attachReceipt = async (pid, file, replacing) => {
    if (!validateReceipt(file)) return;
    try {
      await adminApi.post(`/admin/payments/${pid}/receipt`, { data: await fileToData(file), filename: file.name });
      toast.success(replacing ? "Comprobante reemplazado" : "Comprobante adjuntado"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div data-testid="payments-panel">
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {FILTERS.map(([k, l]) => (
          <button key={k} data-testid={`pay-filter-${k}`} onClick={() => setStatus(k)}
            className={`h-9 px-4 rounded-full text-sm font-medium transition-colors ${status === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
        ))}
        {canConfirm && <Button data-testid="admin-pay-new" onClick={() => setCreate(true)} className="h-9 rounded-full text-xs bg-emerald-600 hover:bg-emerald-700 text-white ml-auto"><Plus className="w-3.5 h-3.5 mr-1" /> Registrar pago</Button>}
      </div>
      {rows === null ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : rows.length === 0 ? (
        <p className="text-sm text-slate-400 py-10 text-center" data-testid="pay-empty">Sin pagos en esta categoría.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((p) => {
            const [lbl, cls] = PST[p.status] || ["", ""];
            const det = detailLines(p);
            return (
              <div key={p.id} data-testid={`pay-admin-row-${p.id}`} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-500 flex items-center gap-1"><Wallet className="w-3 h-3" /> {p.owner_name}{p.owner_code ? ` (${p.owner_code})` : ""}</p>
                    <p className="text-lg font-bold text-slate-900 leading-tight">{money(p.amount)} <span className="text-sm font-normal text-slate-400">· {METHOD_LABEL[p.method] || p.method}</span></p>
                  </div>
                  <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-bold ${cls}`}>{lbl}</span>
                </div>
                <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-600 space-y-0.5">
                  {det.map(([k, v], i) => <p key={i}><span className="text-slate-400">{k}:</span> {v}</p>)}
                  {p.note && <p className="text-slate-500">Nota: {p.note}</p>}
                  <p className="text-slate-400">{p.id} · {p.source === "ADMIN" ? "Registrado por PuntoCell" : "Solicitado por consignador"} · {fmtDate(p.created_at)}</p>
                  {p.status === "CONFIRMADO" && <p className="text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Confirmado por {p.confirmed_by} · {fmtDate(p.confirmed_at)}{(p.confirmation?.reference || p.confirmation?.txid) ? ` · ${p.confirmation.reference || p.confirmation.txid}` : ""}</p>}
                  {p.has_receipt && p.receipt?.attached_by && <p className="text-slate-400 flex items-center gap-1"><FileText className="w-3 h-3" /> Comprobante adjuntado por {p.receipt.attached_by} · {fmtDate(p.receipt.attached_at)}</p>}
                  {p.status === "CANCELADO" && <p className="text-rose-600">Cancelado · {p.cancel_reason || ""}</p>}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {p.status === "PENDIENTE" && canConfirm && <Button data-testid={`pay-admin-confirm-${p.id}`} onClick={() => setConfirm(p)} className="h-9 rounded-lg text-xs bg-slate-900 hover:bg-slate-800"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Confirmar pago/entrega</Button>}
                  <Button data-testid={`pay-admin-share-${p.id}`} onClick={() => openWhatsApp(p.status === "CONFIRMADO" ? buildPaymentConfirmMessage(p) : buildPaymentRequestMessage(p))} className="h-9 rounded-lg text-xs bg-[#25D366] hover:bg-[#1ebe5b] text-white"><MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp</Button>
                  {p.status !== "CANCELADO" && p.has_receipt && <Button variant="outline" data-testid={`pay-admin-view-receipt-${p.id}`} onClick={() => openPaymentReceipt(p.id)} className="h-9 rounded-lg text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"><FileText className="w-3.5 h-3.5 mr-1" /> Ver comprobante</Button>}
                  {p.status !== "CANCELADO" && canConfirm && (
                    <label data-testid={`pay-admin-attach-${p.id}`} className="h-9 rounded-lg text-xs inline-flex items-center px-3 cursor-pointer border border-slate-200 text-slate-600 hover:bg-slate-50">
                      {p.has_receipt ? <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Reemplazar</> : <><Upload className="w-3.5 h-3.5 mr-1" /> Adjuntar comprobante</>}
                      <input type="file" accept={RECEIPT_ACCEPT} onChange={(e) => { const f = e.target.files?.[0]; if (f) attachReceipt(p.id, f, p.has_receipt); e.target.value = ""; }} className="hidden" />
                    </label>
                  )}
                  {p.status === "PENDIENTE" && canConfirm && <Button variant="outline" data-testid={`pay-admin-cancel-${p.id}`} onClick={() => cancel(p.id)} className="h-9 rounded-lg text-xs text-rose-600 border-rose-200 hover:bg-rose-50"><X className="w-3.5 h-3.5 mr-1" /> Cancelar</Button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {confirm && <ConfirmDialog payment={confirm} onClose={() => setConfirm(null)} onDone={() => load()} />}
      {create && <AdminCreateDialog onClose={() => setCreate(false)} onDone={() => load()} />}
    </div>
  );
}
